import type { ListboxItemProps } from "./types";
import { forwardRef, useCallback, useMemo } from "react";
import { useListboxContext } from "./context";
import { isKeyDisabled, isKeySelected } from "./helper";
import clsx from "clsx";

// 自定义 ListboxItem 组件
export const ListboxItem = forwardRef<HTMLElement, ListboxItemProps>(({
    children,
    title,
    description,
    textValue,
    startContent,
    endContent,
    selectedIcon,
    badge,
    badgePlacement = 'right',
    isDisabled: itemDisabled,
    isSelected: itemSelected,
    hideSelectedIcon: itemHideSelectedIcon,
    showDivider,
    href,
    target,
    className,
    classNames,
    onAction,
    onClick,
    onKeyDown,
    ...props
}, ref) => {
    const context = useListboxContext();
    const {
        selectionMode,
        selectedKeys,
        disabledKeys,
        hideSelectedIcon: contextHideSelectedIcon,
        variant = 'flat',
        color = 'default',
        itemClasses,
        onSelectionChange,
        onAction: contextOnAction
    } = context;

    const itemKey = props.key || textValue || (typeof title === 'string' ? title : '');
    const isSelected = itemSelected ?? isKeySelected(itemKey, selectedKeys);
    const isDisabled = itemDisabled ?? isKeyDisabled(itemKey, disabledKeys);
    const hideSelected = itemHideSelectedIcon ?? contextHideSelectedIcon ?? false;
    const isSelectable = selectionMode && selectionMode !== 'none';

    const handleClick = useCallback((e: React.MouseEvent) => {
        if (isDisabled) return;

        e.preventDefault();
        e.stopPropagation();

        // 处理选择逻辑
        if (isSelectable && onSelectionChange) {
            const newSelection = new Set(selectedKeys === "all" ? [] : selectedKeys || []);

            if (selectionMode === 'single') {
                newSelection.clear();
                if (!isSelected) {
                    newSelection.add(itemKey);
                }
            } else if (selectionMode === 'multiple') {
                if (isSelected) {
                    newSelection.delete(itemKey);
                } else {
                    newSelection.add(itemKey);
                }
            }

            onSelectionChange(newSelection);
        }

        // 处理动作
        if (onAction) {
            onAction();
        } else if (contextOnAction) {
            contextOnAction(itemKey);
        }

        // 处理自定义点击事件
        if (onClick) {
            onClick(e);
        }
    }, [isDisabled, isSelectable, onSelectionChange, selectedKeys, selectionMode, isSelected, itemKey, onAction, contextOnAction, onClick]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (isDisabled) return;

        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e as unknown as React.MouseEvent);
        }

        if (onKeyDown) {
            onKeyDown(e);
        }
    }, [isDisabled, handleClick, onKeyDown]);

    const baseClasses = useMemo(() => clsx(
        // 基础样式 - 移除内边距，因为可能会被外部类覆盖
        "relative flex cursor-pointer items-center outline-none tap-highlight-transparent",
        "text-left transition-all duration-200 ease-in-out",

        // 默认内边距和尺寸（如果需要，会被外部 classNames 覆盖）
        "w-full px-3 py-2 gap-2",

        // 圆角
        "rounded-lg",

        // 焦点样式
        "focus:outline-none focus:ring-primary-500/20",

        // 悬停样式（兼容 Tailwind CSS 4）
        "hover:bg-default-100",

        // 选中状态样式
        {
            "bg-primary-50 text-primary-600": isSelected && color === 'primary',
            "bg-secondary-50 text-secondary-600": isSelected && color === 'secondary',
            "bg-success-50 text-success-600": isSelected && color === 'success',
            "bg-warning-50 text-warning-600": isSelected && color === 'warning',
            "bg-danger-50 text-danger-600": isSelected && color === 'danger',
            "bg-default-100 text-default-900": isSelected && color === 'default',
        },

        // 禁用状态样式
        {
            "opacity-50 cursor-not-allowed pointer-events-none": isDisabled,
        },

        // 变体样式
        {
            "border border-default-200": variant === 'bordered' && !isSelected,
            "shadow-sm": variant === 'shadow',
        },

        // 外部类具有最高优先级
        itemClasses?.base,
        classNames?.base,
        className
    ), [isSelected, isDisabled, variant, color, itemClasses?.base, classNames?.base, className]);

    const wrapperClasses = useMemo(() => clsx(
        "flex flex-1 flex-col gap-0 min-w-0",
        itemClasses?.wrapper,
        classNames?.wrapper
    ), [itemClasses?.wrapper, classNames?.wrapper]);

    const titleClasses = useMemo(() => clsx(
        "text-sm font-normal text-foreground",
        {
            "font-medium": isSelected,
        },
        itemClasses?.title,
        classNames?.title
    ), [isSelected, itemClasses?.title, classNames?.title]);

    const descriptionClasses = useMemo(() => clsx(
        "text-xs text-default-500",
        itemClasses?.description,
        classNames?.description
    ), [itemClasses?.description, classNames?.description]);

    const selectedIconClasses = useMemo(() => clsx(
        "text-primary-500 flex-shrink-0",
        itemClasses?.selectedIcon,
        classNames?.selectedIcon
    ), [itemClasses?.selectedIcon, classNames?.selectedIcon]);

    const badgeClasses = useMemo(() => clsx(
        "absolute z-10 flex items-center justify-center",
        "text-xs font-medium text-white bg-danger-500 rounded-full",
        "min-w-4 h-4 px-1",
        {
            // 位置样式
            "-top-1 -right-1": badgePlacement === 'right',
            "-top-1 -left-1": badgePlacement === 'left',
            "-top-2 left-1/2 transform -translate-x-1/2": badgePlacement === 'top',
            "-bottom-2 left-1/2 transform -translate-x-1/2": badgePlacement === 'bottom',
        },
        itemClasses?.badge,
        classNames?.badge
    ), [badgePlacement, itemClasses?.badge, classNames?.badge]);

    const renderSelectedIcon = () => {
        if (hideSelected || !isSelected || !isSelectable) return null;

        if (selectedIcon) return selectedIcon;

        return (
            <svg
                className={selectedIconClasses}
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                    fill="currentColor"
                />
            </svg>
        );
    };

    const ElementType = href ? 'a' : 'li';
    const elementProps = href ? { href, target, rel: target === '_blank' ? 'noopener noreferrer' : undefined } : {};

    return (
        <>
            <ElementType
                ref={ref as React.Ref<HTMLElement> & React.Ref<HTMLAnchorElement> & React.Ref<HTMLLIElement>}
                className={baseClasses}
                role={href ? undefined : "option"}
                aria-selected={isSelectable ? isSelected : undefined}
                aria-disabled={isDisabled}
                tabIndex={isDisabled ? -1 : 0}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                data-selected={isSelected}
                data-disabled={isDisabled}
                data-selectable={isSelectable}
                {...elementProps}
                {...props}
            >
                {startContent && (
                    <div className={clsx(classNames?.startContent)}>
                        {startContent}
                    </div>
                )}

                <div className={wrapperClasses}>
                    {title && (
                        <div className={titleClasses}>
                            {title}
                        </div>
                    )}
                    {description && (
                        <div className={descriptionClasses}>
                            {description}
                        </div>
                    )}
                    {children}
                </div>

                {endContent && (
                    <div className={clsx(classNames?.endContent)}>
                        {endContent}
                    </div>
                )}

                {renderSelectedIcon()}

                {badge && (
                    <div className={badgeClasses}>
                        {badge}
                    </div>
                )}
            </ElementType>

            {showDivider && (
                <hr className="border-default-200 mx-3 my-1" />
            )}
        </>
    );
});

ListboxItem.displayName = "ListboxItem";